import { useEffect, useRef } from 'react'
import { BottomSheet } from './BottomSheet.jsx'

// Time picker stile "wheel", portato da gunsi-tally (senza popover desktop):
// due colonne scrollabili (HH, MM), l'item al centro è quello selezionato.
//
// Props: open, value ('HH:mm'), onChange, onClose, title, nowLabel

const ITEM_H = 40 // px — deve combaciare con .tp-item height nel CSS

function pad(n) { return String(n).padStart(2, '0') }

function parseTime(s) {
  const m = /^(\d{1,2}):(\d{1,2})$/.exec(s ?? '')
  if (!m) return [0, 0]
  return [Math.max(0, Math.min(23, parseInt(m[1], 10))),
          Math.max(0, Math.min(59, parseInt(m[2], 10)))]
}

export function TimePicker({ open, value, onChange, onClose, title, nowLabel }) {
  const [hh, mm] = parseTime(value)

  function commit(newHh, newMm) {
    onChange(`${pad(newHh)}:${pad(newMm)}`)
  }

  function resetToNow() {
    const d = new Date()
    onChange(`${pad(d.getHours())}:${pad(d.getMinutes())}`)
    onClose()
  }

  return (
    <BottomSheet open={open} onClose={onClose} className="tp-sheet">
      <div className="tp-panel">
      <div className="tp-header">
        <span className="tp-title">{title}</span>
        <button type="button" className="tp-reset" onClick={resetToNow}>{nowLabel}</button>
        <button type="button" className="tp-close" onClick={onClose} aria-label="close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="tp-columns">
        <Wheel length={24} value={hh} onChange={(v) => commit(v, mm)} open={open} />
        <Wheel length={60} value={mm} onChange={(v) => commit(hh, v)} open={open} />
        <div className="tp-center" aria-hidden="true">
          <span className="tp-sep">:</span>
        </div>
      </div>
      </div>
    </BottomSheet>
  )
}

function Wheel({ length, value, onChange, open }) {
  const ref = useRef(null)
  const lastEmitted = useRef(value)
  const scrollTimer = useRef(null)

  // Padding sopra/sotto = (contenitore − item)/2 così primo/ultimo item
  // possono arrivare al centro
  useEffect(() => {
    if (!open || !ref.current) return
    const el = ref.current
    function applyPadding() {
      const p = Math.max(0, (el.clientHeight - ITEM_H) / 2)
      el.style.paddingTop    = `${p}px`
      el.style.paddingBottom = `${p}px`
    }
    applyPadding()
    window.addEventListener('resize', applyPadding)
    return () => window.removeEventListener('resize', applyPadding)
  }, [open])

  // All'apertura salta al valore corrente (senza animazione)
  useEffect(() => {
    if (!open || !ref.current) return
    const id = requestAnimationFrame(() => {
      if (!ref.current) return
      ref.current.scrollTop = value * ITEM_H
      lastEmitted.current = value
    })
    return () => cancelAnimationFrame(id)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Valore cambiato dall'esterno → scroll morbido
  useEffect(() => {
    if (!open || !ref.current) return
    if (value === lastEmitted.current) return
    ref.current.scrollTo({ top: value * ITEM_H, behavior: 'smooth' })
    lastEmitted.current = value
  }, [value, open])

  function onScroll() {
    if (!ref.current) return
    clearTimeout(scrollTimer.current)
    scrollTimer.current = setTimeout(() => {
      if (!ref.current) return
      const idx = Math.round(ref.current.scrollTop / ITEM_H)
      const clamped = Math.max(0, Math.min(length - 1, idx))
      if (clamped !== lastEmitted.current) {
        lastEmitted.current = clamped
        onChange(clamped)
      }
    }, 60)
  }

  function pickItem(i) {
    if (!ref.current) return
    ref.current.scrollTo({ top: i * ITEM_H, behavior: 'smooth' })
  }

  const items = []
  for (let i = 0; i < length; i++) items.push(i)

  return (
    <div className="tp-col" ref={ref} onScroll={onScroll}>
      {items.map(i => (
        <button
          key={i}
          type="button"
          className={`tp-item${i === value ? ' selected' : ''}`}
          onClick={() => pickItem(i)}
        >{pad(i)}</button>
      ))}
    </div>
  )
}
